import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

export interface JsonEditorProps {
  value: unknown;
  onChange: (value: unknown) => void;
  label?: string;
  height?: string;
  readOnly?: boolean;
}

export function JsonEditor({
  value,
  onChange,
  label,
  height = '300px',
  readOnly = false
}: JsonEditorProps) {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      setText(JSON.stringify(value, null, 2));
      setError(null);
    } catch {
      // If value can't be stringified, keep current text
    }
  }, [value]);

  const handleChange = (newText: string) => {
    setText(newText);
    try {
      const parsed = JSON.parse(newText);
      setError(null);
      onChange(parsed);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {label && (
        <Label className="mb-2">{label}</Label>
      )}
      <div className="flex-1 relative">
        <Textarea
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          readOnly={readOnly}
          className={cn(
            "font-mono text-sm resize-none",
            error && "border-destructive focus-visible:ring-destructive",
            readOnly && "bg-muted"
          )}
          style={{ height }}
          spellCheck={false}
        />
        {error && (
          <div className="absolute bottom-2 left-2 right-2 bg-destructive/10 border border-destructive/30 rounded px-2 py-1 text-xs text-destructive">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

export default JsonEditor;
